<template>
  <div class="page-container">
    <ModalLoading :isLoading="isLoadingAny" />

    <!-- ═══ CABECERA ═══════════════════════════════════════════════ -->
    <div class="page-header">
      <div style="display:flex;gap:8px;align-items:center;flex-wrap:wrap;flex:1">
        <select v-if="esCeo" class="form-select" style="min-width:180px"
                v-model="filtroSucursal" @change="onCambioSucursal">
          <option value="">Todas las sucursales</option>
          <option v-for="s in sucursalesCeo" :key="s.id_sucursal" :value="s.id_sucursal">
            {{ s.nombre_administrativo }}
          </option>
        </select>
        <input v-model="busqueda" type="text"
               :placeholder="tabActiva === 'devoluciones' ? 'Buscar por nota de crédito o N° factura…' : tabActiva === 'retornos' ? 'Buscar por producto o nota de crédito…' : 'Buscar por factura, empleado o descripción…'"
               class="form-input busqueda-top" />
      </div>
      <button class="btn-primary" @click="abrirNueva">
        <i class="bi bi-plus"></i> Nueva Gestión
      </button>
    </div>

    <!-- ═══ PESTAÑAS ══════════════════════════════════════════════════ -->
    <div class="tabs-bar">
      <button class="tab-btn" :class="{ active: tabActiva === 'devoluciones' }"
              @click="cambiarTab('devoluciones')">
        <i class="bi bi-receipt"></i> Devoluciones
        <span class="tab-count">{{ devoluciones.length }}</span>
      </button>
      <button class="tab-btn" :class="{ active: tabActiva === 'anulaciones' }"
              @click="cambiarTab('anulaciones')">
        <i class="bi bi-x-circle"></i> Anulaciones
        <span class="tab-count">{{ ventasCanceladas.length }}</span>
      </button>
      <button class="tab-btn" :class="{ active: tabActiva === 'retornos' }"
              @click="cambiarTab('retornos')">
        <i class="bi bi-arrow-repeat"></i> Retornos
        <span class="tab-count" :class="{ 'tab-count-pending': retornosPendientes.length }">
          {{ retornosPendientes.length }}
        </span>
      </button>
      <button class="tab-btn" :class="{ active: tabActiva === 'historial' }"
              @click="cambiarTab('historial')">
        <i class="bi bi-clock-history"></i> Historial NC
      </button>
    </div>

    <!-- ═══ TAB: DEVOLUCIONES / CAMBIOS ══════════════════════════════ -->
    <template v-if="tabActiva === 'devoluciones'">
      <!-- Barra de acciones del tab -->
      <div v-if="devolucionesFiltradas.length" class="tab-actions-bar">
        <button class="btn-outline-sm" @click="descargarPDFPendientes"
                :disabled="descargandoPDFPendientes" title="Descargar resumen NCs pendientes">
          <i :class="descargandoPDFPendientes ? 'bi bi-hourglass-split' : 'bi bi-file-earmark-pdf'"></i>
          Resumen NCs pendientes
        </button>
      </div>
      <div v-if="!devolucionesFiltradas.length" class="empty-state">
        <i class="bi bi-arrow-return-left"></i>
        <p>No hay notas de crédito registradas.</p>
      </div>
      <div v-else class="table-container">
        <table class="data-table">
          <thead>
            <tr>
              <th>Nota de Crédito</th>
              <th>Factura</th>
              <th>Tipo</th>
              <th>Monto</th>
              <th>Motivo</th>
              <th>Empleado</th>
              <th>Fecha</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="d in devolucionesPaginadas" :key="d.id_devolucion">
              <td class="mono">{{ d.numero_nota_credito }}</td>
              <td class="mono">{{ getNumFactura(d) }}</td>
              <td>
                <span class="badge-nc"><i class="bi bi-receipt"></i> Crédito</span>
              </td>
              <td>L. {{ fmtNum(d.monto_devuelto) }}</td>
              <td class="text-muted motivo-td">{{ d.motivo || '—' }}</td>
              <td>{{ d.Usuarios ? d.Usuarios.nombre + ' ' + d.Usuarios.apellido : '—' }}</td>
              <td>{{ fmtFecha(d.created_at) }}</td>
              <td style="display:flex;gap:4px">
                <button class="accion-btn accion-ver" @click="verDetalle(d)" title="Ver detalle">
                  <i class="bi bi-eye"></i>
                </button>
                <button class="accion-btn accion-exportar" @click="descargarPDF(d)" title="Descargar PDF"
                        :disabled="descargando === d.id_devolucion">
                  <i :class="descargando === d.id_devolucion ? 'bi bi-hourglass-split' : 'bi bi-file-earmark-pdf'"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="pagination-wrapper">
        <div class="pagination-info">
          Mostrando {{ devolucionesFiltradas.length === 0 ? 0 : (currentPage-1)*pageSize+1 }} a
          {{ Math.min(currentPage*pageSize, devolucionesFiltradas.length) }}
          de {{ devolucionesFiltradas.length }} registros
        </div>
        <div class="pagination-container">
          <button class="pagination-button" :disabled="currentPage===1" @click="previousPage">Anterior</button>
          <button class="pagination-button" :disabled="currentPage===totalPages" @click="nextPage">Siguiente</button>
        </div>
      </div>
    </template>

    <!-- ═══ TAB: ANULACIONES ══════════════════════════════════════════ -->
    <template v-else-if="tabActiva === 'anulaciones'">
      <div v-if="!anulacionesFiltradas.length" class="empty-state">
        <i class="bi bi-x-circle"></i>
        <p>No hay ventas anuladas registradas.</p>
      </div>
      <div v-else class="table-container">
        <table class="data-table">
          <thead>
            <tr>
              <th>Factura</th>
              <th>Monto</th>
              <th>Descripción</th>
              <th>Empleado</th>
              <th>Fecha</th>
              <th>Estado</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="v in anulacionesPaginadas" :key="v.id_venta">
              <td class="mono">{{ v.numero_factura !== 'N/A' ? v.numero_factura : v.id_venta }}</td>
              <td>L. {{ fmtNum(v.total) }}</td>
              <td class="text-muted motivo-td">{{ motivoAnulacion(v.descripcion) }}</td>
              <td>{{ v.nombre }}</td>
              <td>{{ fmtFecha(v.fechaHora) }}</td>
              <td><span class="badge-anulada"><i class="bi bi-x-circle"></i> Anulada</span></td>
              <td style="display:flex;gap:4px">
                <button class="accion-btn accion-ver" @click="verDetalleAnulacion(v)" title="Ver detalle">
                  <i class="bi bi-eye"></i>
                </button>
                <button class="accion-btn accion-exportar" @click="descargarReporteCancelacion(v)"
                        :disabled="descargandoAnulacion === v.id_venta" title="Reporte de cancelación">
                  <i :class="descargandoAnulacion === v.id_venta ? 'bi bi-hourglass-split' : 'bi bi-file-earmark-pdf'"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="pagination-wrapper">
        <div class="pagination-info">
          Mostrando {{ anulacionesFiltradas.length === 0 ? 0 : (currentPage-1)*pageSize+1 }} a
          {{ Math.min(currentPage*pageSize, anulacionesFiltradas.length) }}
          de {{ anulacionesFiltradas.length }} registros
        </div>
        <div class="pagination-container">
          <button class="pagination-button" :disabled="currentPage===1" @click="previousPage">Anterior</button>
          <button class="pagination-button" :disabled="currentPage===totalPages" @click="nextPage">Siguiente</button>
        </div>
      </div>
    </template>

    <!-- ═══ TAB: RETORNOS ═══════════════════════════════════════════ -->
    <template v-else-if="tabActiva === 'retornos'">
      <div v-if="!retornosFiltrados.length" class="empty-state">
        <i class="bi bi-arrow-repeat"></i>
        <p>No hay retornos registrados.</p>
      </div>
      <div v-else class="table-container">
        <table class="data-table">
          <thead>
            <tr>
              <th>Nota de Crédito</th>
              <th>Producto</th>
              <th>Cantidad</th>
              <th>Fecha</th>
              <th>Estado</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="r in retornosPaginados" :key="r.id" :class="{ 'row-pendiente': r.destino === 'Pendiente' }">
              <td class="mono">{{ r.devoluciones?.numero_nota_credito || '—' }}</td>
              <td>{{ r.nombre_producto }}</td>
              <td>{{ r.cantidad }}</td>
              <td>{{ fmtFecha(r.created_at) }}</td>
              <td>
                <span :class="badgeRetorno(r.destino)">
                  <i :class="iconoRetorno(r.destino)"></i> {{ r.destino }}
                </span>
              </td>
              <td>
                <template v-if="r.destino === 'Pendiente'">
                  <div style="display:flex;gap:4px;flex-wrap:wrap">
                    <button class="btn-retorno btn-stock"   @click="abrirResolverRetorno(r, 'Stock')"
                            :disabled="resolviendoRetorno === r.id" title="Devolver a inventario">
                      <i class="bi bi-boxes"></i> Stock
                    </button>
                    <button class="btn-retorno btn-descarte" @click="abrirResolverRetorno(r, 'Descarte')"
                            :disabled="resolviendoRetorno === r.id" title="Marcar como descarte">
                      <i class="bi bi-trash3"></i> Descarte
                    </button>
                    <button class="btn-retorno btn-proveedor" @click="abrirResolverRetorno(r, 'Proveedor')"
                            :disabled="resolviendoRetorno === r.id" title="Devolver a proveedor">
                      <i class="bi bi-truck"></i> Proveedor
                    </button>
                  </div>
                </template>
                <span v-else class="text-muted" style="font-size:12px">
                  {{ fmtFecha(r.resuelto_at) }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="pagination-wrapper">
        <div class="pagination-info">
          Mostrando {{ retornosFiltrados.length === 0 ? 0 : (currentPage-1)*pageSize+1 }} a
          {{ Math.min(currentPage*pageSize, retornosFiltrados.length) }}
          de {{ retornosFiltrados.length }} registros
        </div>
        <div class="pagination-container">
          <button class="pagination-button" :disabled="currentPage===1" @click="previousPage">Anterior</button>
          <button class="pagination-button" :disabled="currentPage===totalPages" @click="nextPage">Siguiente</button>
        </div>
      </div>
    </template>

    <!-- ═══ TAB: HISTORIAL NC ══════════════════════════════════════ -->
    <template v-else-if="tabActiva === 'historial'">
      <div v-if="!historialNC.length" class="empty-state">
        <i class="bi bi-clock-history"></i>
        <p>No hay notas de crédito aplicadas en el historial.</p>
      </div>
      <div v-else class="table-container">
        <table class="data-table">
          <thead>
            <tr>
              <th>Nota de Crédito</th>
              <th>Monto</th>
              <th>Motivo</th>
              <th>Factura aplicada</th>
              <th>Tipo pago</th>
              <th>Fecha</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="h in historialPaginado" :key="h.id_devolucion + '-' + h.codigo_factura">
              <td class="mono">{{ h.numero_nota_credito }}</td>
              <td>L. {{ fmtNum(h.monto_devuelto) }}</td>
              <td class="text-muted motivo-td">{{ h.motivo || '—' }}</td>
              <td class="mono">{{ h.codigo_factura || '—' }}</td>
              <td>{{ h.tipo_factura || '—' }}</td>
              <td>{{ fmtFecha(h.fecha_aplicada) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="pagination-wrapper">
        <div class="pagination-info">
          Mostrando {{ historialFiltrado.length === 0 ? 0 : (currentPage-1)*pageSize+1 }} a
          {{ Math.min(currentPage*pageSize, historialFiltrado.length) }}
          de {{ historialFiltrado.length }} registros
        </div>
        <div class="pagination-container">
          <button class="pagination-button" :disabled="currentPage===1" @click="previousPage">Anterior</button>
          <button class="pagination-button" :disabled="currentPage===totalPages" @click="nextPage">Siguiente</button>
        </div>
      </div>
    </template>

    <!-- ═══ MODAL RESOLVER RETORNO ══════════════════════════════════ -->
    <div v-if="modalRetorno.visible" class="modal-overlay">
      <div class="modal-confirm" style="max-width:440px">
        <button class="modal-close-x" @click="modalRetorno.visible = false">✕</button>
        <div class="modal-confirm-icon">
          <i :class="iconoRetorno(modalRetorno.destino)" style="font-size:28px"></i>
        </div>
        <h3 style="margin:8px 0 4px">{{ labelRetorno(modalRetorno.destino) }}</h3>
        <p style="color:#6c757d;margin:0 0 16px;font-size:14px">
          <strong>{{ modalRetorno.item?.nombre_producto }}</strong> — {{ modalRetorno.item?.cantidad }} unid.
        </p>
        <div style="margin-bottom:14px">
          <label style="font-size:13px;font-weight:600;display:block;margin-bottom:4px">Notas (opcional)</label>
          <textarea v-model="modalRetorno.notas" rows="2"
                    class="form-input" style="resize:none;width:100%"
                    :placeholder="modalRetorno.destino === 'Proveedor' ? 'N° de guía, proveedor, observaciones…' : 'Observaciones…'"></textarea>
        </div>
        <div style="display:flex;gap:8px;justify-content:flex-end">
          <button class="btn-secondary" @click="modalRetorno.visible = false">Cancelar</button>
          <button :class="'btn-retorno-confirm btn-' + modalRetorno.destino.toLowerCase()"
                  @click="confirmarRetorno" :disabled="resolviendoRetorno">
            <i class="bi bi-check-lg"></i> Confirmar
          </button>
        </div>
      </div>
    </div>

    <!-- ═══ MODAL PRINCIPAL — WIZARD ═══════════════════════════════ -->
    <div v-if="modalNueva" class="modal-overlay">
      <div class="modal-large">

        <!-- Header del modal -->
        <div class="modal-header">
          <div class="wizard-title-wrap">
            <h2>{{ tituloPaso }}</h2>
            <!-- Indicador de pasos -->
            <div v-if="tipo" class="step-indicator">
              <span v-for="(s, i) in breadcrumbs" :key="i"
                    :class="['step-dot', { active: i === paso, done: i < paso }]">
                <span class="step-label">{{ s }}</span>
              </span>
            </div>
          </div>
          <button class="btn-close-x" @click="cerrarModal"><i class="bi bi-x-lg"></i></button>
        </div>

        <div class="modal-body">

          <!-- ── PASO 0: Elegir tipo ─────────────────────────────── -->
          <div v-if="paso === 0" class="tipo-grid">
            <div class="tipo-card" @click="elegirTipo('devolucion')">
              <i class="bi bi-arrow-return-left tipo-icon"></i>
              <h3>Devolución</h3>
              <p>El cliente devuelve uno o más productos. Genera Nota de Crédito SAR.</p>
            </div>
            <div class="tipo-card anulacion-card" @click="elegirTipo('anulacion')">
              <i class="bi bi-x-circle tipo-icon"></i>
              <h3>Anulación Total</h3>
              <p>Cancela la venta completa y repone todo el inventario. No genera nota de crédito SAR.</p>
            </div>
          </div>

          <!-- ── PASO 1: Buscar venta ───────────────────────────── -->
          <div v-if="paso === 1">
            <div class="form-group">
              <label>N° de Factura <span class="hint-label">(SAR o código interno)</span></label>
              <div class="buscar-row">
                <input v-model="form.numero_factura" type="text" class="form-input flex1"
                       placeholder="Ej: 001-001-01-00000001" @keyup.enter="buscarVenta"
                       :disabled="buscandoVenta" />
                <button class="btn-primary" @click="buscarVenta"
                        :disabled="buscandoVenta || !form.numero_factura.trim()">
                  <i v-if="buscandoVenta" class="bi bi-hourglass-split"></i>
                  <i v-else class="bi bi-search"></i>
                  {{ buscandoVenta ? 'Buscando…' : 'Buscar' }}
                </button>
              </div>
            </div>
            <div class="form-group">
              <label>Fecha <span class="hint-label">(opcional — para confirmar)</span></label>
              <input v-model="form.fecha" type="date" class="form-input" style="max-width:200px" />
            </div>
            <div v-if="errorVenta" class="alert-error">
              <i class="bi bi-exclamation-circle"></i> {{ errorVenta }}
            </div>
            <div v-if="ventaTotalmenteDevuelta" class="alert-warning">
              <i class="bi bi-exclamation-triangle-fill"></i>
              Esta venta ya fue <strong>totalmente devuelta</strong>. No quedan ítems disponibles para generar una nueva NC.
            </div>
            <div v-if="itemsVenta.length" class="venta-encontrada">
              <i class="bi bi-check-circle-fill"></i>
              Venta encontrada &mdash; <strong>{{ itemsVenta.length }}</strong> producto(s) disponible(s)
              <span v-if="tipo === 'anulacion'" class="hint-label"> (todos serán revertidos)</span>
            </div>
          </div>

          <!-- ── PASO 2 (devol): Seleccionar ítems ─────────────── -->
          <div v-if="tipo === 'devolucion' && paso === 2">
            <p class="hint">Selecciona los productos que el cliente devuelve y la cantidad exacta.</p>
            <table class="items-table">
              <thead>
                <tr>
                  <th style="width:36px">Sel.</th>
                  <th>Producto</th>
                  <th>Disponible</th>
                  <th>A devolver</th>
                  <th>Precio</th>
                  <th style="text-align:right">Subtotal</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in itemsVenta" :key="item.id_producto"
                    :class="{ 'row-selected': item.seleccionado }">
                  <td><input type="checkbox" v-model="item.seleccionado" class="check-item" /></td>
                  <td>
                    <span class="prod-nombre">{{ item.nombre_producto }}</span>
                    <span v-if="item.codigo" class="prod-codigo">{{ item.codigo }}</span>
                  </td>
                  <td class="mono">{{ item.cantidad_disponible }}</td>
                  <td>
                    <input v-if="item.seleccionado" v-model.number="item.cantidad_a_devolver"
                           type="number" min="1" :max="item.cantidad_disponible"
                           class="input-qty" />
                    <span v-else class="text-muted">—</span>
                  </td>
                  <td>L. {{ fmtNum(item.precio_unitario) }}</td>
                  <td style="text-align:right">
                    <span v-if="item.seleccionado">
                      L. {{ fmtNum(item.cantidad_a_devolver * item.precio_unitario) }}
                    </span>
                    <span v-else class="text-muted">—</span>
                  </td>
                </tr>
              </tbody>
            </table>
            <div v-if="itemsSeleccionados.length" class="subtotal-bar">
              <span>{{ itemsSeleccionados.length }} producto(s) seleccionado(s)</span>
              <span class="subtotal-val">Total a devolver: <strong>L. {{ fmtNum(montoDevolucion) }}</strong></span>
            </div>
          </div>

          <!-- ── PASO 3 (devol): Resolución ─────────────────────── -->
          <div v-if="tipo === 'devolucion' && paso === 3">

            <!-- Motivo -->
            <div class="form-group">
              <label>Motivo</label>
              <textarea v-model="form.motivo" class="form-textarea" rows="2"
                        placeholder="Ej: Producto defectuoso, vencido, error de pedido…"></textarea>
            </div>

            <!-- CxC -->
            <div class="form-group">
              <label>¿Afecta Cuenta por Cobrar?</label>
              <select v-model="form.afecta_cxc" class="form-select">
                <option :value="false">No (venta de contado)</option>
                <option :value="true">Sí (venta a crédito)</option>
              </select>
            </div>

            <!-- Resumen de ítems devueltos -->
            <div class="resumen-box">
              <h4>Productos a devolver</h4>
              <table class="items-table compact">
                <thead><tr><th>Producto</th><th>Cant.</th><th>Precio</th><th style="text-align:right">Total</th></tr></thead>
                <tbody>
                  <tr v-for="item in itemsSeleccionados" :key="item.id_producto">
                    <td>{{ item.nombre_producto }}</td>
                    <td class="mono">{{ item.cantidad_a_devolver }}</td>
                    <td>L. {{ fmtNum(item.precio_unitario) }}</td>
                    <td style="text-align:right">L. {{ fmtNum(item.cantidad_a_devolver * item.precio_unitario) }}</td>
                  </tr>
                </tbody>
              </table>
              <div class="monto-total">
                Crédito generado: <strong>L. {{ fmtNum(montoDevolucion) }}</strong>
              </div>
            </div>
          </div>

          <!-- ── PASO 2 (anulación): Confirmar ─────────────────── -->
          <div v-if="tipo === 'anulacion' && paso === 2">
            <div class="alert-danger">
              <i class="bi bi-exclamation-triangle-fill"></i>
              Esta acción <strong>cancela la venta completa</strong> y repone todo el stock.
              <strong>No se puede deshacer.</strong>
            </div>

            <div class="resumen-box" style="margin-top:16px">
              <h4>Productos que regresan al inventario</h4>
              <table class="items-table compact">
                <thead><tr><th>Producto</th><th>Cantidad</th><th style="text-align:right">Valor</th></tr></thead>
                <tbody>
                  <tr v-for="item in itemsVenta" :key="item.id_producto">
                    <td>{{ item.nombre_producto }}</td>
                    <td class="mono">{{ item.cantidad_disponible }}</td>
                    <td style="text-align:right">L. {{ fmtNum(item.precio_unitario * item.cantidad_disponible) }}</td>
                  </tr>
                </tbody>
              </table>
              <div class="monto-total">
                Total anulado: <strong>L. {{ fmtNum(totalAnulacion) }}</strong>
              </div>
            </div>

            <div class="form-group" style="margin-top:20px">
              <label>Motivo de la anulación <span style="color:#dc3545">*</span></label>
              <textarea v-model="form.motivo" class="form-textarea" rows="3"
                        placeholder="Describe el motivo de la anulación…"></textarea>
            </div>
          </div>

        </div><!-- /modal-body -->

        <!-- Footer del wizard -->
        <div class="modal-footer">
          <button class="btn-cancel" @click="irAtras">
            {{ paso === 0 ? 'Cancelar' : '← Atrás' }}
          </button>

          <!-- Paso 1 → siguiente (cuando ya hay venta) -->
          <button v-if="paso === 1 && itemsVenta.length" class="btn-primary" @click="avanzarDesdePaso1">
            Siguiente <i class="bi bi-arrow-right"></i>
          </button>

          <!-- Paso 2 devol → siguiente -->
          <button v-if="tipo === 'devolucion' && paso === 2" class="btn-primary"
                  @click="avanzarPaso3" :disabled="!itemsSeleccionados.length">
            Siguiente <i class="bi bi-arrow-right"></i>
          </button>

          <!-- Paso 3 devol → procesar -->
          <button v-if="tipo === 'devolucion' && paso === 3"
                  class="btn-confirmar" @click="confirmarDevolucion"
                  :disabled="guardando">
            <i class="bi bi-check-lg"></i>
            {{ guardando ? 'Procesando…' : 'Procesar Devolución' }}
          </button>

          <!-- Paso 2 anulación → confirmar -->
          <button v-if="tipo === 'anulacion' && paso === 2"
                  class="btn-danger" @click="confirmarAnulacion"
                  :disabled="guardando || !form.motivo.trim()">
            <i class="bi bi-x-circle"></i>
            {{ guardando ? 'Procesando…' : 'Confirmar Anulación' }}
          </button>
        </div>

      </div>
    </div>

    <!-- ═══ MODAL DETALLE NC ════════════════════════════════════════ -->
    <div v-if="modalDetalle && detalleActivo" class="modal-overlay">
      <div class="modal-large">
        <div class="modal-header">
          <h2>{{ detalleActivo.numero_nota_credito }}</h2>
          <button class="btn-close-x" @click="modalDetalle = false"><i class="bi bi-x-lg"></i></button>
        </div>
        <div class="modal-body">
          <div class="info-row">
            <span><span class="badge-nc">Nota de Crédito</span></span>
            <span>Monto: <strong>L. {{ fmtNum(detalleActivo.monto_devuelto) }}</strong></span>
            <span>Fecha: <strong>{{ fmtFecha(detalleActivo.created_at) }}</strong></span>
            <span v-if="detalleActivo.afecta_cxc" class="badge-info">Afecta CxC</span>
          </div>
          <div class="form-group" v-if="detalleActivo.motivo">
            <strong>Motivo:</strong> {{ detalleActivo.motivo }}
          </div>
          <table class="items-table" style="margin-top:16px">
            <thead><tr><th>Producto</th><th>Cant.</th><th>Precio</th><th>Total</th></tr></thead>
            <tbody>
              <tr v-for="d in detalleActivo.detalle_devoluciones" :key="d.id_detalle_devolucion">
                <td>{{ d.nombre_producto }}</td>
                <td>{{ d.cantidad_devuelta }}</td>
                <td>L. {{ fmtNum(d.precio_unitario) }}</td>
                <td>L. {{ fmtNum(d.total_devuelto) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- ═══ MODAL DETALLE ANULACIÓN ══════════════════════════════════ -->
    <div v-if="modalDetalleAnulacion && anulacionActiva" class="modal-overlay">
      <div class="modal-large" style="max-width:520px">
        <div class="modal-header">
          <h2>Detalle de Anulación</h2>
          <button class="btn-close-x" @click="modalDetalleAnulacion = false"><i class="bi bi-x-lg"></i></button>
        </div>
        <div class="modal-body">
          <div class="info-row">
            <span><span class="badge-anulada"><i class="bi bi-x-circle"></i> Anulación Total</span></span>
            <span>Monto: <strong>L. {{ fmtNum(anulacionActiva.total) }}</strong></span>
            <span>Fecha: <strong>{{ fmtFecha(anulacionActiva.fechaHora) }}</strong></span>
          </div>
          <div class="resumen-box" style="margin-top:14px">
            <div style="display:flex;flex-direction:column;gap:8px;font-size:14px">
              <div><span style="color:#888">Factura SAR:</span> <strong>{{ anulacionActiva.numero_factura }}</strong></div>
              <div><span style="color:#888">Empleado:</span> <strong>{{ anulacionActiva.nombre }}</strong></div>
              <div><span style="color:#888">Cliente:</span> <strong>{{ anulacionActiva.cliente }}</strong></div>
              <div v-if="anulacionActiva.descripcion"><span style="color:#888">Motivo:</span> {{ motivoAnulacion(anulacionActiva.descripcion) }}</div>
            </div>
          </div>
          <p style="margin-top:14px;font-size:13px;color:#888">
            <i class="bi bi-info-circle"></i>
            En Honduras, la anulación cancela la factura SAR original. No genera Nota de Crédito — el monto es devuelto directamente al cliente.
          </p>
        </div>
        <div class="modal-footer">
          <button class="btn-confirmar" @click="descargarReporteCancelacion(anulacionActiva)"
                  :disabled="descargandoAnulacion === anulacionActiva.id_venta">
            <i :class="descargandoAnulacion === anulacionActiva.id_venta ? 'bi bi-hourglass-split' : 'bi bi-file-earmark-pdf'"></i>
            Reporte de Cancelación
          </button>
          <button class="btn-cancel" @click="modalDetalleAnulacion = false">Cerrar</button>
        </div>
      </div>
    </div>

    <!-- Confirmación devolución -->
    <div v-if="confirmDev.visible" class="modal-overlay">
      <div class="modal-large" style="max-width:460px">
        <div class="modal-header">
          <h2>Confirmar Devolución</h2>
        </div>
        <div class="modal-body">
          <p>Esta operación es <strong>irreversible</strong> — afecta stock e inventario.</p>
          <p>Monto a acreditar: <strong>L. {{ fmtNum(montoDevolucion) }}</strong></p>
          <p>¿Confirmas el proceso?</p>
        </div>
        <div class="modal-footer">
          <button class="btn-cancel" @click="confirmDev.visible = false">Cancelar</button>
          <button class="btn-confirmar" @click="ejecutarDevolucion" :disabled="guardando">
            {{ guardando ? 'Procesando…' : 'Sí, procesar' }}
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { getDevoluciones, crearDevolucion, getItemsVenta, descargarPDFDevolucion, getRetornos, resolverRetorno, getHistorialNC } from '../../services/featuresSolicitudes.js';
import { getSucursalesbyEmmpresaSumm } from '../../services/sucursalesSolicitudes.js';
import AdminVentas from '../../services/Soliadminventa.js';
import { useToast } from 'vue-toastification';
import { setPageTitle } from '@/components/pageMetadata';
import ModalLoading from "@/components/ModalLoading.vue";
const { getSocket } = require('@/services/socket');

export default {
  name: 'DevolucionesPage',
  components: { ModalLoading },
  data() {
    const token = localStorage.getItem('auth');
    const toast = useToast();
    return {
      toast,
      descargando: null,
      id_usuario: token ? JSON.parse(atob(token.split('.')[1])).id_usuario : null,
      esCeo: Number(localStorage.getItem('role') || 0) === 4,
      sucursalesCeo: [],
      filtroSucursal: '',
      currentPage: 1,
      pageSize: 10,

      // Tabs
      tabActiva: 'devoluciones',

      // Tabla devoluciones
      devoluciones: [], cargando: false,
      busqueda: '',
      modalDetalle: false, detalleActivo: null,

      // Tabla anulaciones
      ventasCanceladas: [], cargandoAnulaciones: false,
      modalDetalleAnulacion: false, anulacionActiva: null,
      descargandoAnulacion: null,

      // Tabla retornos
      retornos: [], cargandoRetornos: false,
      resolviendoRetorno: null,
      modalRetorno: { visible: false, item: null, destino: '', notas: '' },

      // Historial NC
      historialNC: [], cargandoHistorial: false,
      descargandoPDFPendientes: false,

      // Wizard
      modalNueva: false,
      tipo: '',          // 'devolucion' | 'anulacion'
      paso: 0,
      ventaId: null,
      form: {
        numero_factura: '',
        fecha: '',
        motivo: '',
        afecta_cxc: false,
      },
      itemsVenta: [],
      buscandoVenta: false,
      errorVenta: '',
      guardando: false,
      confirmDev: { visible: false },
    };
  },

  computed: {
    isLoadingAny() {
      return this.cargando || this.cargandoAnulaciones || this.cargandoRetornos || this.cargandoHistorial;
    },
    devolucionesFiltradas() {
      if (!this.busqueda) return this.devoluciones;
      const b = this.busqueda.toLowerCase();
      return this.devoluciones.filter(d =>
        d.numero_nota_credito?.toLowerCase().includes(b) ||
        this.getNumFactura(d).toLowerCase().includes(b)
      );
    },
    anulacionesFiltradas() {
      if (!this.busqueda) return this.ventasCanceladas;
      const b = this.busqueda.toLowerCase();
      return this.ventasCanceladas.filter(v =>
        v.numero_factura?.toLowerCase().includes(b) ||
        v.nombre?.toLowerCase().includes(b) ||
        v.descripcion?.toLowerCase().includes(b) ||
        String(v.id_venta).includes(b)
      );
    },
    ventaTotalmenteDevuelta() {
      return this.ventaId && !this.buscandoVenta && !this.errorVenta && this.itemsVenta.length === 0;
    },
    retornosFiltrados() {
      if (!this.busqueda) return this.retornos;
      const b = this.busqueda.toLowerCase();
      return this.retornos.filter(r =>
        r.nombre_producto?.toLowerCase().includes(b) ||
        r.devoluciones?.numero_nota_credito?.toLowerCase().includes(b)
      );
    },
    retornosPendientes() {
      return this.retornos.filter(r => r.destino === 'Pendiente');
    },
    historialFiltrado() {
      const q = this.busqueda.trim().toLowerCase();
      if (!q) return this.historialNC;
      return this.historialNC.filter(h =>
        h.numero_nota_credito?.toLowerCase().includes(q) ||
        h.codigo_factura?.toLowerCase().includes(q) ||
        h.motivo?.toLowerCase().includes(q)
      );
    },
    devolucionesPaginadas() {
      const s = (this.currentPage - 1) * this.pageSize;
      return this.devolucionesFiltradas.slice(s, s + this.pageSize);
    },
    anulacionesPaginadas() {
      const s = (this.currentPage - 1) * this.pageSize;
      return this.anulacionesFiltradas.slice(s, s + this.pageSize);
    },
    retornosPaginados() {
      const s = (this.currentPage - 1) * this.pageSize;
      return this.retornosFiltrados.slice(s, s + this.pageSize);
    },
    historialPaginado() {
      const s = (this.currentPage - 1) * this.pageSize;
      return this.historialFiltrado.slice(s, s + this.pageSize);
    },
    totalActual() {
      if (this.tabActiva === 'devoluciones') return this.devolucionesFiltradas.length;
      if (this.tabActiva === 'anulaciones')  return this.anulacionesFiltradas.length;
      if (this.tabActiva === 'retornos')     return this.retornosFiltrados.length;
      return this.historialFiltrado.length;
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.totalActual / this.pageSize));
    },
    itemsSeleccionados() {
      return this.itemsVenta.filter(i => i.seleccionado && i.cantidad_a_devolver > 0);
    },
    montoDevolucion() {
      return this.itemsSeleccionados.reduce((s, i) => s + i.cantidad_a_devolver * i.precio_unitario, 0);
    },
    totalAnulacion() {
      return this.itemsVenta.reduce((s, i) => s + i.cantidad_disponible * i.precio_unitario, 0);
    },
    tituloPaso() {
      if (this.paso === 0) return 'Nueva Gestión';
      if (this.tipo === 'devolucion') {
        if (this.paso === 1) return 'Buscar Venta';
        if (this.paso === 2) return 'Seleccionar Productos';
        if (this.paso === 3) return 'Resolución';
      }
      if (this.tipo === 'anulacion') {
        if (this.paso === 1) return 'Buscar Venta';
        if (this.paso === 2) return 'Confirmar Anulación';
      }
      return 'Nueva Gestión';
    },
    breadcrumbs() {
      if (this.tipo === 'devolucion') return ['Tipo', 'Buscar', 'Productos', 'Resolución'];
      if (this.tipo === 'anulacion')  return ['Tipo', 'Buscar', 'Confirmar'];
      return [];
    },
  },

  watch: {
    busqueda() { this.currentPage = 1; },
    tabActiva() { this.currentPage = 1; },
  },

  async mounted() {
    setPageTitle('Devoluciones');
    if (this.esCeo) {
      this.sucursalesCeo = await getSucursalesbyEmmpresaSumm(this.id_usuario);
      if (!Array.isArray(this.sucursalesCeo)) this.sucursalesCeo = [];
    }
    await Promise.all([this.cargar(), this.cargarAnulaciones(), this.cargarRetornos()]);
    const _s = getSocket();
    if (_s) { this._wsRefresh = () => { this.cargar(); this.cargarRetornos(); }; _s.on('devoluciones:refresh', this._wsRefresh); }

    // Redirección desde AdminVentas — abre wizard en paso 0 para que el usuario elija tipo
    const q = this.$route?.query || {};
    if (q.factura || q.id_venta) {
      this.form.numero_factura = q.factura || q.id_venta || '';
      this.modalNueva = true;
      if (q.tipo === 'devolucion' || q.tipo === 'anulacion') {
        this.tipo = q.tipo;
        this.paso = 1;
        if (q.tipo === 'devolucion') this.buscarVenta();
      }
      this.$router.replace({ name: 'Devoluciones', query: {} });
    }
  },
  beforeUnmount() {
    const _s = getSocket();
    if (_s && this._wsRefresh) _s.off('devoluciones:refresh', this._wsRefresh);
  },

  methods: {
    previousPage() { if (this.currentPage > 1) this.currentPage--; },
    nextPage()     { if (this.currentPage < this.totalPages) this.currentPage++; },
    // ─── CARGA ─────────────────────────────────────────────────────
    async cargar() {
      this.cargando = true;
      try {
        this.devoluciones = await getDevoluciones(this.id_usuario, this.filtroSucursal);
      } catch (e) { console.error(e); } finally { this.cargando = false; }
    },
    async cargarAnulaciones() {
      this.cargandoAnulaciones = true;
      try {
        const todas = await AdminVentas.obtenerVentas();
        this.ventasCanceladas = (todas || []).filter(v => v.estado === 'Cancelada');
      } catch (e) { console.error(e); } finally { this.cargandoAnulaciones = false; }
    },
    async cargarRetornos() {
      this.cargandoRetornos = true;
      try { this.retornos = (await getRetornos(this.id_usuario))?.data || []; }
      catch (e) { console.error(e); } finally { this.cargandoRetornos = false; }
    },
    async cargarHistorialNC() {
      this.cargandoHistorial = true;
      try {
        const res = await getHistorialNC(this.id_usuario, this.filtroSucursal);
        this.historialNC = Array.isArray(res) ? res : (res?.data || []);
      } catch (e) {
        this.toast.error('Error al cargar historial de NCs');
        console.error(e);
      } finally {
        this.cargandoHistorial = false;
      }
    },
    async descargarPDFPendientes() {
      this.descargandoPDFPendientes = true;
      try {
        const { jsPDF } = await import('jspdf');
        const autoTable = (await import('jspdf-autotable')).default;
        const pendientes = this.devoluciones.filter(d => !d.aplicada);
        const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'letter' });
        doc.setFontSize(14);
        doc.text('Resumen de Notas de Crédito Pendientes', 14, 18);
        doc.setFontSize(10);
        doc.text(`Generado: ${new Date().toLocaleString('es-HN')}`, 14, 25);
        autoTable(doc, {
          startY: 30,
          head: [['Nota de Crédito', 'Tipo', 'Monto', 'Motivo', 'Fecha']],
          body: pendientes.map(d => [
            d.numero_nota_credito || '—',
            d.tipo_devolucion || '—',
            `L. ${Number(d.monto_devuelto || 0).toFixed(2)}`,
            d.motivo || '—',
            this.fmtFecha(d.created_at),
          ]),
          styles: { fontSize: 9 },
          headStyles: { fillColor: [192, 157, 98] },
          alternateRowStyles: { fillColor: [252, 249, 243] },
        });
        const totalPendiente = pendientes.reduce((s, d) => s + Number(d.monto_devuelto || 0), 0);
        const y = doc.lastAutoTable.finalY + 6;
        doc.setFontSize(11);
        doc.text(`Total pendiente: L. ${totalPendiente.toFixed(2)}  |  NCs: ${pendientes.length}`, 14, y);
        doc.save(`NCs-pendientes-${new Date().toISOString().slice(0, 10)}.pdf`);
      } catch (e) {
        this.toast.error('Error al generar PDF');
        console.error(e);
      } finally {
        this.descargandoPDFPendientes = false;
      }
    },
    onCambioSucursal() {
      this.cargar();
      if (this.tabActiva === 'historial') this.cargarHistorialNC();
    },
    cambiarTab(tab) {
      this.tabActiva = tab;
      this.busqueda = '';
      if (tab === 'anulaciones' && !this.ventasCanceladas.length) this.cargarAnulaciones();
      if (tab === 'retornos' && !this.retornos.length) this.cargarRetornos();
      if (tab === 'historial') this.cargarHistorialNC();
    },
    abrirResolverRetorno(item, destino) {
      this.modalRetorno = { visible: true, item, destino, notas: '' };
    },
    async confirmarRetorno() {
      const { item, destino, notas } = this.modalRetorno;
      this.resolviendoRetorno = item.id;
      try {
        await resolverRetorno(item.id, this.id_usuario, destino, notas);
        this.toast.success(`Retorno marcado como: ${destino}`);
        this.modalRetorno.visible = false;
        await this.cargarRetornos();
      } catch (e) {
        this.toast.error('Error al resolver retorno: ' + e.message);
      } finally {
        this.resolviendoRetorno = null;
      }
    },
    badgeRetorno(destino) {
      return {
        'badge-retorno-pendiente': destino === 'Pendiente',
        'badge-retorno-stock':     destino === 'Stock',
        'badge-retorno-descarte':  destino === 'Descarte',
        'badge-retorno-proveedor': destino === 'Proveedor',
      };
    },
    iconoRetorno(destino) {
      const map = { Pendiente: 'bi bi-clock', Stock: 'bi bi-boxes', Descarte: 'bi bi-trash3', Proveedor: 'bi bi-truck' };
      return map[destino] || 'bi bi-question';
    },
    labelRetorno(destino) {
      const map = { Stock: 'Devolver a inventario', Descarte: 'Marcar como descarte', Proveedor: 'Devolver a proveedor' };
      return map[destino] || destino;
    },

    // ─── WIZARD — NAVEGACIÓN ────────────────────────────────────────
    abrirNueva() {
      this.resetWizard();
      this.modalNueva = true;
    },
    cerrarModal() {
      this.modalNueva = false;
      this.resetWizard();
    },
    resetWizard() {
      this.tipo = '';
      this.paso = 0;
      this.ventaId = null;
      this.form = { numero_factura: '', fecha: '', motivo: '', afecta_cxc: false };
      this.itemsVenta = [];
      this.errorVenta = '';
    },
    elegirTipo(t) {
      this.tipo = t;
      this.paso = 1;
      // Si viene con factura pre-cargada desde AdminVentas, auto-buscar
      if (this.form.numero_factura) this.buscarVenta();
    },
    irAtras() {
      if (this.paso === 0) { this.cerrarModal(); return; }
      if (this.paso === 1) { this.paso = 0; this.itemsVenta = []; this.errorVenta = ''; return; }
      if (this.paso === 2) { this.paso = 1; return; }
      if (this.paso === 3) { this.paso = 2; return; }
    },
    avanzarDesdePaso1() {
      this.paso = 2;
    },
    avanzarPaso3() {
      const sel = this.itemsVenta.filter(i => i.seleccionado);
      if (!sel.length) { this.toast.warning('Selecciona al menos un producto a devolver.'); return; }
      for (const item of sel) {
        const qty = Number(item.cantidad_a_devolver);
        if (!qty || qty <= 0) {
          this.toast.warning(`Ingresa una cantidad válida para "${item.nombre_producto}".`); return;
        }
        if (qty > item.cantidad_disponible) {
          this.toast.warning(`No puedes devolver más de ${item.cantidad_disponible} de "${item.nombre_producto}".`); return;
        }
      }
      this.paso = 3;
    },

    // ─── BÚSQUEDA DE VENTA ──────────────────────────────────────────
    async buscarVenta() {
      if (!this.form.numero_factura.trim()) return;
      this.buscandoVenta = true;
      this.errorVenta = '';
      this.itemsVenta = [];
      try {
        const res = await getItemsVenta(this.form.numero_factura.trim(), this.form.fecha);
        if (!res.items || !res.items.length) {
          this.errorVenta = this.tipo === 'anulacion'
            ? 'Esta venta ya fue anulada o no tiene ítems.'
            : 'Esta venta no tiene productos disponibles para devolver.';
          return;
        }
        this.ventaId = res.id_venta;
        this.itemsVenta = res.items.map(i => ({ ...i, seleccionado: false, cantidad_a_devolver: 1 }));
      } catch (e) {
        this.errorVenta = e.message || 'No se encontró la venta o ya fue cancelada.';
      } finally { this.buscandoVenta = false; }
    },

    // ─── PROCESAR DEVOLUCIÓN ───────────────────────────────────────
    confirmarDevolucion() {
      this.confirmDev.visible = true;
    },
    async ejecutarDevolucion() {
      this.confirmDev.visible = false;
      await this.guardarDevolucion();
    },
    async guardarDevolucion() {
      this.guardando = true;
      try {
        const motivoFinal = this.form.motivo || '';

        const items = this.itemsSeleccionados.map(i => ({
          id_producto:        i.id_producto,
          nombre_producto:    i.nombre_producto,
          cantidad_devuelta:  i.cantidad_a_devolver,
          cantidad_disponible: i.cantidad_disponible,
          precio_unitario:    i.precio_unitario,
          total_devuelto:     i.cantidad_a_devolver * i.precio_unitario,
        }));

        const res = await crearDevolucion(this.id_usuario, {
          id_venta:   this.ventaId,
          motivo:     motivoFinal,
          afecta_cxc: this.form.afecta_cxc,
          items,
        });

        this.toast.success(`Devolución procesada. Nota de Crédito: ${res.numero_nota_credito}`);
        this.cerrarModal();
        await this.cargar();
      } catch (e) {
        this.toast.error('Error: ' + (e.message || 'Error al procesar'));
      } finally { this.guardando = false; }
    },

    // ─── PROCESAR ANULACIÓN ─────────────────────────────────────────
    async confirmarAnulacion() {
      if (!this.form.motivo.trim()) { this.toast.warning('Ingresa el motivo de la anulación.'); return; }
      this.guardando = true;
      try {
        const res = await AdminVentas.cancelarVenta(this.ventaId, this.form.motivo);
        this.toast.success('Venta anulada exitosamente.');
        // Fix 5: avisar si no había caja activa para descontar
        if (res?.advertencia_caja) {
          setTimeout(() => this.toast.warning(`⚠️ ${res.advertencia_caja}`), 400);
        }
        // Fix 6: avisar si requiere reversa bancaria
        if (res?.requiere_reversa_bancaria) {
          setTimeout(() => this.toast.info('ℹ️ Esta venta fue pagada por transferencia. Realiza la devolución bancaria al cliente de forma manual.'), 800);
        }
        this.cerrarModal();
      } catch (e) {
        this.toast.error('Error: ' + (e.message || 'Error al anular la venta'));
      } finally { this.guardando = false; }
    },

    // ─── DETALLE / PDF ──────────────────────────────────────────────
    verDetalle(d) { this.detalleActivo = d; this.modalDetalle = true; },
    async descargarPDF(d) {
      this.descargando = d.id_devolucion;
      try {
        await descargarPDFDevolucion(d.id_devolucion);
        this.toast.success(`PDF descargado: ${d.numero_nota_credito}`);
      } catch (e) { this.toast.error('Error al generar PDF: ' + e.message); }
      finally { this.descargando = null; }
    },

    verDetalleAnulacion(v) { this.anulacionActiva = v; this.modalDetalleAnulacion = true; },
    async descargarReporteCancelacion(v) {
      this.descargandoAnulacion = v.id_venta;
      try {
        const blob = await AdminVentas.obtenerReporteCancelacion(v.id_venta);
        const url  = URL.createObjectURL(blob);
        const a    = document.createElement('a');
        a.href     = url;
        a.download = `anulacion_${v.numero_factura !== 'N/A' ? v.numero_factura : v.id_venta}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
        this.toast.success('Reporte de cancelación descargado');
      } catch (e) { this.toast.error('Error al generar reporte: ' + e.message); }
      finally { this.descargandoAnulacion = null; }
    },

    // ─── HELPERS ───────────────────────────────────────────────────
    getNumFactura(d) {
      const f = d.Ventas?.facturas?.[0];
      return f?.numero_factura_SAR?.[0]?.numero_factura_SAR || f?.codigo_factura || `Venta #${d.id_venta}`;
    },
    fmtNum(n) { return Number(n || 0).toLocaleString('es-HN', { minimumFractionDigits: 2 }); },
    fmtFecha(f) { return new Date(f).toLocaleDateString('es-HN'); },
    // Extrae solo el motivo limpio de la descripción de una venta anulada
    // Formato: "ANULADA: motivo | descripcion_original..." → "motivo"
    motivoAnulacion(desc) {
      if (!desc) return '—';
      const m = desc.match(/^ANULADA:\s*([^|]+)/);
      return m ? m[1].trim() : desc.split(' | ')[0].trim() || '—';
    },
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,100..900;1,100..900&display=swap");
* { font-family: "Montserrat", sans-serif; box-sizing: border-box; }

/* ── Page ── */
.page-container { padding: 16px; width: 100%; overflow-x: hidden; background: #ffffff; min-height: 100vh; color: #212529; }
.page-header { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 20px; }
.busqueda-top { flex: 1; max-width: 420px; }

/* ── Inputs ── */
.busqueda-top, .form-input { padding: 8px 12px; font-size: 14px; border-radius: 10px; border: 1px solid #ced4da; outline: none; background: white; color: #212529; transition: all .3s; }
.form-input:focus, select:focus, textarea:focus { border-color: #c09d62; box-shadow: 0 0 0 3px rgba(192,157,98,.2); }
.form-select, select { padding: 8px 12px; border-radius: 10px; border: 1px solid #ced4da; font-size: 14px; cursor: pointer; background: white; color: #212529; }
.form-textarea { width: 100%; padding: 8px 12px; border-radius: 10px; border: 1px solid #ced4da; font-size: 14px; resize: vertical; outline: none; background: white; color: #212529; }
.buscar-row { display: flex; gap: 8px; }
.flex1 { flex: 1; }

/* ── Buttons ── */
.btn-primary { background: #c09d62; color: white; padding: 8px 16px; border: none; border-radius: 10px; font-size: 14px; font-weight: 600; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; transition: all .3s; }
.btn-primary:hover:not(:disabled) { background: #a38655; transform: scale(1.02); }
.btn-primary:disabled { opacity: .5; cursor: not-allowed; }
.btn-confirmar { background: #198754; color: white; padding: 8px 16px; border: none; border-radius: 10px; font-size: 14px; font-weight: 600; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; transition: all .3s; }
.btn-confirmar:hover:not(:disabled) { background: #146c43; transform: scale(1.02); }
.btn-confirmar:disabled { opacity: .5; cursor: not-allowed; }

/* Botones de acción en filas de tabla */
.accion-btn { display: inline-flex; align-items: center; justify-content: center; width: 32px; height: 32px; border: none; border-radius: 6px; cursor: pointer; color: #fff; font-size: 14px; transition: opacity .2s, transform .2s; flex-shrink: 0; }
.accion-btn:hover:not(:disabled) { opacity: .85; transform: scale(1.08); }
.accion-btn:disabled { opacity: .45; cursor: not-allowed; }
.accion-ver      { background: #0e7490; }
.accion-exportar { background: #198754; }
.btn-cancel { background: #f1f1f1; color: #555; padding: 8px 16px; border: none; border-radius: 10px; font-size: 14px; cursor: pointer; transition: all .2s; }
.btn-cancel:hover { background: #e0e0e0; }
.btn-danger { background: #dc3545; color: white; padding: 8px 16px; border: none; border-radius: 10px; font-size: 14px; font-weight: 600; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; transition: all .2s; }
.btn-danger:hover:not(:disabled) { background: #b02a37; }
.btn-danger:disabled { opacity: .5; cursor: not-allowed; }
.btn-icon { background: white !important; border: 1px solid #ced4da; color: #555 !important; width: 32px; height: 32px; border-radius: 8px; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: all .2s; }
.btn-icon:hover { background: #f0f0f0 !important; border-color: #bbb; }
.btn-icon.info { background: #cff4fc !important; color: #055160 !important; border-color: #9eeaf9; }
.btn-icon.info:hover { background: #055160 !important; color: white !important; }
.btn-icon.danger { background: #f8d7da !important; color: #842029 !important; border-color: #f1aeb5; }
.btn-icon.danger:hover { background: #842029 !important; color: white !important; }
.btn-icon.warning { background: #fff3cd !important; color: #856404 !important; border-color: #ffda6a; }
.btn-icon.warning:hover { background: #856404 !important; color: white !important; }
.btn-icon.success { background: #d1e7dd !important; color: #0f5132 !important; border-color: #a3cfbb; }
.btn-icon.success:hover { background: #0f5132 !important; color: white !important; }
.btn-icon-sm { background: none; border: none; color: #dc3545; cursor: pointer; padding: 2px 6px; border-radius: 6px; }
.btn-icon-sm:hover { background: rgba(220,53,69,.1); }
.btn-close-x { background: none; border: none; color: #888; cursor: pointer; font-size: 18px; display: flex; align-items: center; padding: 4px; border-radius: 6px; transition: color .2s; }
.btn-close-x:hover { color: #333; }

/* ── Table ── */
.table-container { border-radius: 12px; overflow-x: auto; -webkit-overflow-scrolling: touch; border: 1px solid #e9ecef; }
.data-table { width: 100%; border-collapse: collapse; font-size: 14px; min-width: 640px; }
.data-table thead { background: #f8f8f8; }
.data-table th { padding: 12px 14px; text-align: left; font-weight: 600; color: #555; font-size: 12px; letter-spacing: .5px; text-transform: uppercase; border-bottom: 1px solid #e9ecef; white-space: nowrap; }
.data-table td { padding: 12px 14px; border-bottom: 1px solid #f0f0f0; vertical-align: middle; }
.data-table tbody tr:hover { background: #fafafa; }
.data-table tbody tr:last-child td { border-bottom: none; }
.mono { font-family: monospace; font-size: 13px; }
.text-muted { color: #888; font-size: 13px; }
.motivo-td { max-width: 200px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

/* ── Badges ── */
.badge-nc { background: rgba(192,157,98,.12); color: #a38655; padding: 3px 8px; border-radius: 6px; font-size: 12px; display: inline-flex; align-items: center; gap: 4px; }
.badge-info { background: rgba(13,202,240,.1); color: #0c8599; padding: 3px 8px; border-radius: 6px; font-size: 12px; }
.badge-anulada { background: rgba(220,53,69,.1); color: #b02a37; padding: 3px 8px; border-radius: 6px; font-size: 12px; display: inline-flex; align-items: center; gap: 4px; }

/* Tabs */
.tabs-bar { display: flex; gap: 4px; margin-bottom: 16px; border-bottom: 2px solid #dee2e6; }
.tab-btn { background: none; border: none; border-bottom: 3px solid transparent; padding: 8px 16px; font-size: 14px; font-weight: 600; cursor: pointer; color: #6c757d; display: inline-flex; align-items: center; gap: 6px; transition: all .2s; margin-bottom: -2px; font-family: "Montserrat", sans-serif; border-radius: 4px 4px 0 0; }
.tab-btn:hover { color: #495057; background: #f8f9fa; }
.tab-btn.active { color: #c09d62; border-bottom-color: #c09d62; background: none; }
.tab-count { background: #e9ecef; color: #495057; border-radius: 10px; padding: 1px 7px; font-size: 11px; font-weight: 700; }

/* ── Empty / loading ── */
.loading { display: flex; justify-content: center; padding: 40px; }
.loading-small { display: flex; align-items: center; gap: 8px; color: #888; padding: 12px 0; font-size: 14px; }
.empty-state { text-align: center; padding: 60px 20px; color: #aaa; }
.empty-state i { font-size: 48px; display: block; margin-bottom: 12px; }
.spinner { width: 36px; height: 36px; border: 3px solid #f0f0f0; border-top-color: #c09d62; border-radius: 50%; animation: spin .7s linear infinite; }
.spinner-sm { width: 18px; height: 18px; border: 2px solid #eee; border-top-color: #c09d62; border-radius: 50%; animation: spin .7s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }

/* ── Modal ── */
.modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,.45); display: flex; align-items: center; justify-content: center; z-index: 1000; padding: 20px; }
.modal-large { background: white; border-radius: 16px; width: 100%; max-width: 780px; max-height: 90vh; display: flex; flex-direction: column; box-shadow: 0 8px 32px rgba(0,0,0,.18); overflow: hidden; }
.modal-header { display: flex; align-items: flex-start; justify-content: space-between; padding: 20px 24px 16px; border-bottom: 1px solid #f0f0f0; gap: 12px; }
.modal-header h2 { font-size: 18px; font-weight: 700; margin: 0; color: #333; }
.modal-body { padding: 20px 24px; overflow-y: auto; flex: 1; }
.modal-footer { display: flex; justify-content: flex-end; gap: 10px; padding: 16px 24px; border-top: 1px solid #f0f0f0; background: #fafafa; }

/* ── Wizard título + step indicator ── */
.wizard-title-wrap { display: flex; flex-direction: column; gap: 8px; }
.step-indicator { display: flex; align-items: center; gap: 0; }
.step-dot { display: flex; flex-direction: column; align-items: center; position: relative; }
.step-dot:not(:last-child)::after {
  content: '';
  position: absolute;
  left: calc(50% + 8px);
  top: 8px;
  width: 32px;
  height: 2px;
  background: #e0e0e0;
}
.step-dot:not(:last-child).done::after { background: #c09d62; }
.step-dot::before {
  content: '';
  width: 16px; height: 16px;
  border-radius: 50%;
  background: #e0e0e0;
  display: block;
  margin: 0 20px;
}
.step-dot.done::before { background: #c09d62; }
.step-dot.active::before { background: #c09d62; box-shadow: 0 0 0 3px rgba(192,157,98,.25); }
.step-label { font-size: 10px; color: #999; margin-top: 4px; white-space: nowrap; }
.step-dot.active .step-label, .step-dot.done .step-label { color: #c09d62; font-weight: 600; }

/* ── Tipo selector ── */
.tipo-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
.tipo-card { border: 2px solid #e9ecef; border-radius: 14px; padding: 24px 20px; cursor: pointer; transition: all .2s; text-align: center; }
.tipo-card:hover { border-color: #c09d62; background: rgba(192,157,98,.04); }
.tipo-card.anulacion-card:hover { border-color: #dc3545; background: rgba(220,53,69,.04); }
.tipo-icon { font-size: 36px; color: #c09d62; margin-bottom: 12px; display: block; }
.anulacion-card .tipo-icon { color: #dc3545; }
.tipo-card h3 { font-size: 16px; font-weight: 700; margin: 0 0 8px; color: #333; }
.tipo-card p { font-size: 13px; color: #888; margin: 0; line-height: 1.5; }

/* ── Paso 1 ── */
.hint-label { font-size: 12px; color: #999; font-weight: 400; }
.alert-error   { background: rgba(220,53,69,.08); border: 1px solid rgba(220,53,69,.3); color: #dc3545; padding: 10px 14px; border-radius: 10px; font-size: 14px; display: flex; align-items: center; gap: 8px; margin-top: 8px; }
.alert-warning { background: rgba(255,193,7,.1); border: 1px solid rgba(255,193,7,.4); color: #856404; padding: 10px 14px; border-radius: 10px; font-size: 14px; display: flex; align-items: center; gap: 8px; margin-top: 8px; }
.alert-danger { background: rgba(220,53,69,.08); border: 1px solid rgba(220,53,69,.25); color: #721c24; padding: 14px 16px; border-radius: 12px; font-size: 14px; display: flex; align-items: flex-start; gap: 10px; line-height: 1.5; }
.alert-danger i { font-size: 18px; flex-shrink: 0; margin-top: 1px; }
.venta-encontrada { background: rgba(25,135,84,.08); border: 1px solid rgba(25,135,84,.25); color: #0f5132; padding: 10px 14px; border-radius: 10px; font-size: 14px; display: flex; align-items: center; gap: 8px; margin-top: 8px; }
.venta-encontrada i { color: #198754; }

/* ── Items table ── */
.items-table { width: 100%; border-collapse: collapse; font-size: 14px; margin-top: 12px; }
.items-table th { padding: 8px 10px; background: #f8f8f8; font-weight: 600; color: #555; font-size: 12px; text-transform: uppercase; letter-spacing: .3px; border-bottom: 1px solid #e9ecef; }
.items-table td { padding: 10px; border-bottom: 1px solid #f0f0f0; vertical-align: middle; }
.items-table.compact td, .items-table.compact th { padding: 7px 10px; }
.row-selected { background: rgba(192,157,98,.05); }
.check-item { width: 16px; height: 16px; accent-color: #c09d62; cursor: pointer; }
.prod-nombre { display: block; font-weight: 500; }
.prod-codigo { display: block; font-size: 11px; color: #aaa; font-family: monospace; }
.input-qty { width: 70px; padding: 4px 8px; border: 1px solid #ced4da; border-radius: 6px; font-size: 13px; text-align: center; }
.input-qty:focus { border-color: #c09d62; outline: none; }
.subtotal-bar { display: flex; justify-content: space-between; align-items: center; background: #f8f5ef; border-radius: 8px; padding: 10px 14px; margin-top: 12px; font-size: 14px; }
.subtotal-val { font-weight: 600; color: #a38655; }

/* ── Resolución ── */
.res-selector { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.res-card { border: 2px solid #e9ecef; border-radius: 12px; padding: 16px; cursor: pointer; display: flex; align-items: flex-start; gap: 12px; transition: all .2s; position: relative; }
.res-card:hover { border-color: #c09d62; }
.res-card.active { border-color: #c09d62; background: rgba(192,157,98,.05); }
.res-icon { font-size: 22px; color: #c09d62; flex-shrink: 0; margin-top: 2px; }
.res-card strong { display: block; font-size: 14px; margin-bottom: 4px; color: #333; }
.res-card p { font-size: 12px; color: #888; margin: 0; line-height: 1.4; }
.res-check { position: absolute; top: 10px; right: 10px; color: #c09d62; font-size: 16px; }
.form-group { margin-bottom: 14px; }
.form-group label { display: block; font-weight: 600; font-size: 13px; color: #555; margin-bottom: 6px; }

/* ── Resumen box ── */
.resumen-box { background: #f9f9f9; border-radius: 12px; padding: 16px; margin-top: 12px; }
.resumen-box h4 { font-size: 14px; font-weight: 700; color: #555; margin: 0 0 10px; }
.monto-total { text-align: right; margin-top: 12px; font-size: 15px; color: #333; padding-top: 8px; border-top: 1px solid #e9ecef; }

/* ── Cambio section ── */

.balance-result { font-size: 15px; border-top: 1px solid #e5dfd3; padding-top: 8px; margin-top: 2px; }
.balance-result.positivo strong { color: #198754; }
.balance-result.negativo strong { color: #dc3545; }

/* ── Info row (detalle) ── */
.info-row { display: flex; flex-wrap: wrap; gap: 12px; align-items: center; margin-bottom: 14px; font-size: 14px; }

/* ── Dark mode — misma paleta que el sistema: #1e1e1e / #2d2d2d / #404040 ── */
.app-wrapper.dark .page-container { background: #1e1e1e; color: #fff; }

/* Tabla */
.app-wrapper.dark .table-container { border-color: #404040; }
.app-wrapper.dark .data-table thead { background: #2d2d2d; }
.app-wrapper.dark .data-table th { color: #c09d62; border-color: #404040; }
.app-wrapper.dark .data-table td { border-color: #404040; color: #fff; background: #1e1e1e; }
.app-wrapper.dark .data-table tbody tr:hover td { background: #2d2d2d; }
.app-wrapper.dark .text-muted { color: #999; }

/* Inputs */
.app-wrapper.dark .form-input,
.app-wrapper.dark .form-select,
.app-wrapper.dark .form-textarea,
.app-wrapper.dark .busqueda-top { background: #2d2d2d; border-color: #404040; color: #fff; }
.app-wrapper.dark .form-input::placeholder,
.app-wrapper.dark .busqueda-top::placeholder { color: #777; }
.app-wrapper.dark .form-group label { color: #c09d62; }
.app-wrapper.dark .hint-label { color: #777; }

/* Modal */
.app-wrapper.dark .modal-large { background: #2d2d2d; color: #fff; }
.app-wrapper.dark .modal-header { border-color: #404040; background: #2d2d2d; }
.app-wrapper.dark .modal-header h2 { color: #fff; }
.app-wrapper.dark .modal-body { background: #2d2d2d; }
.app-wrapper.dark .modal-footer { background: #242424; border-color: #404040; }

/* Step indicator */
.app-wrapper.dark .step-dot::before { background: #404040; }
.app-wrapper.dark .step-dot::after { background: #404040; }
.app-wrapper.dark .step-dot.done::before { background: #c09d62; }
.app-wrapper.dark .step-dot.done::after { background: #c09d62; }
.app-wrapper.dark .step-dot.active::before { background: #c09d62; box-shadow: 0 0 0 3px rgba(192,157,98,.25); }
.app-wrapper.dark .step-label { color: #777; }
.app-wrapper.dark .step-dot.active .step-label,
.app-wrapper.dark .step-dot.done .step-label { color: #c09d62; }

/* Tipo cards */
.app-wrapper.dark .tipo-card { border-color: #404040; background: #353535; }
.app-wrapper.dark .tipo-card:hover { border-color: #c09d62; background: rgba(192,157,98,.08); }
.app-wrapper.dark .anulacion-card:hover { border-color: #dc3545; background: rgba(220,53,69,.06); }
.app-wrapper.dark .tipo-card h3 { color: #fff; }
.app-wrapper.dark .tipo-card p { color: #aaa; }

/* Resolución cards */
.app-wrapper.dark .res-card { border-color: #404040; background: #353535; }
.app-wrapper.dark .res-card:hover { border-color: #c09d62; }
.app-wrapper.dark .res-card.active { border-color: #c09d62; background: rgba(192,157,98,.08); }
.app-wrapper.dark .res-card strong { color: #fff; }
.app-wrapper.dark .res-card p { color: #aaa; }

/* Resumen / items */
.app-wrapper.dark .resumen-box { background: #353535; }
.app-wrapper.dark .resumen-box h4 { color: #c09d62; }
.app-wrapper.dark .monto-total { color: #fff; border-color: #404040; }
.app-wrapper.dark .items-table th { background: #2d2d2d; color: #c09d62; border-color: #404040; }
.app-wrapper.dark .items-table td { border-color: #404040; color: #fff; }
.app-wrapper.dark .row-selected { background: rgba(192,157,98,.06); }
.app-wrapper.dark .subtotal-bar { background: #2d2d2d; color: #fff; }
.app-wrapper.dark .subtotal-val { color: #c09d62; }

/* Alertas */
.app-wrapper.dark .alert-error   { background: rgba(220,53,69,.12); border-color: rgba(220,53,69,.35); }
.app-wrapper.dark .alert-warning { background: rgba(255,193,7,.08); border-color: rgba(255,193,7,.3); color: #ffc107; }
.app-wrapper.dark .alert-danger { background: rgba(220,53,69,.12); border-color: rgba(220,53,69,.25); color: #f0a0a0; }
.app-wrapper.dark .venta-encontrada { background: rgba(25,135,84,.1); border-color: rgba(25,135,84,.3); color: #6de0a0; }
.app-wrapper.dark .venta-encontrada i { color: #6de0a0; }

/* Cambio */

.app-wrapper.dark .balance-result { border-color: #404040; }

/* Badges */
.app-wrapper.dark .badge-nc { background: rgba(192,157,98,.15); color: #d4b07a; }
.app-wrapper.dark .badge-info { background: rgba(13,202,240,.12); color: #7de8ff; }
.app-wrapper.dark .badge-anulada { background: rgba(220,53,69,.12); color: #f0a0a0; }

/* Tabs */
.app-wrapper.dark .tabs-bar { border-bottom-color: #404040; }
.app-wrapper.dark .tab-btn { color: #888; }
.app-wrapper.dark .tab-btn:hover { color: #ccc; background: #2d2d2d; }
.app-wrapper.dark .tab-btn.active { color: #c09d62; border-bottom-color: #c09d62; }
.app-wrapper.dark .tab-count { background: #2d2d2d; color: #aaa; }

/* Botones */
.app-wrapper.dark .btn-cancel { background: #2d2d2d; color: #ccc; border: 1px solid #404040; }
.app-wrapper.dark .btn-cancel:hover { background: #353535; }
.app-wrapper.dark .btn-close-x { color: #999; }
.app-wrapper.dark .btn-close-x:hover { color: #fff; }
.app-wrapper.dark .btn-icon { background: #2d2d2d !important; border-color: #404040; color: #9e9e9e !important; }
.app-wrapper.dark .btn-icon:hover { background: #3d3d3d !important; border-color: #666; color: #fff !important; }
.app-wrapper.dark .btn-icon.info { background: #0c2d48 !important; color: #7dd3fc !important; border-color: #0369a1; }
.app-wrapper.dark .btn-icon.info:hover { background: #0369a1 !important; color: white !important; }
.app-wrapper.dark .btn-icon.danger { background: #4c1d1d !important; color: #fca5a5 !important; border-color: #7f1d1d; }
.app-wrapper.dark .btn-icon.danger:hover { background: #7f1d1d !important; color: white !important; }
.app-wrapper.dark .btn-icon.warning { background: #2a2215 !important; color: #ffe082 !important; border-color: #856404; }
.app-wrapper.dark .btn-icon.warning:hover { background: #856404 !important; color: white !important; }
.app-wrapper.dark .btn-icon.success { background: #1b4332 !important; color: #6ee7b7 !important; border-color: #065f46; }
.app-wrapper.dark .btn-icon.success:hover { background: #065f46 !important; color: white !important; }

/* Info row (detalle) */
.app-wrapper.dark .info-row { color: #ccc; }

@media (max-width: 600px) {
  .tipo-grid, .res-selector { grid-template-columns: 1fr; }
  .prod-picker-grid { grid-template-columns: 1fr 1fr; }
}

/* ── Responsive móvil ─────────────────────────────────────────── */
@media (max-width: 640px) {
  .page-container { padding: 12px 10px; }

  /* Header en columna */
  .page-header { flex-direction: column; align-items: stretch; gap: 8px; }
  .busqueda-top { max-width: none; width: 100%; }
  .btn-primary { justify-content: center; }

  /* Tabs: scroll horizontal si no caben */
  .tabs-bar { overflow-x: auto; flex-wrap: nowrap; -webkit-overflow-scrolling: touch;
              padding-bottom: 2px; gap: 4px; }
  .tabs-bar::-webkit-scrollbar { height: 3px; }
  .tabs-bar::-webkit-scrollbar-thumb { background: #c09d62; border-radius: 2px; }
  .tab-btn { white-space: nowrap; flex-shrink: 0; padding: 8px 12px; font-size: 13px; }

  /* Tabla más compacta */
  .data-table { font-size: 13px; }
  .data-table th, .data-table td { padding: 9px 10px; }
  .motivo-td { max-width: 120px; }

  /* Modal ancho completo */
  .modal-large { max-width: 100%; max-height: 100vh; border-radius: 0; }
  .modal-body { padding: 14px 16px; }

  /* Wizard steps */
  .wizard-step { padding: 14px 16px; }
}

/* ── Retornos ─────────────────────────────────────────────── */
.tab-count-pending { background: #ffc107; color: #000; }
.row-pendiente td:first-child { border-left: 3px solid #ffc107; }

.badge-retorno-pendiente { background:#fff3cd; color:#856404; border-radius:10px; padding:2px 8px; font-size:11px; font-weight:700; }
.badge-retorno-stock      { background:#d1e7dd; color:#0a3622; border-radius:10px; padding:2px 8px; font-size:11px; font-weight:700; }
.badge-retorno-descarte   { background:#f8d7da; color:#58151c; border-radius:10px; padding:2px 8px; font-size:11px; font-weight:700; }
.badge-retorno-proveedor  { background:#cfe2ff; color:#084298; border-radius:10px; padding:2px 8px; font-size:11px; font-weight:700; }

.btn-retorno { border:none; border-radius:6px; padding:4px 10px; font-size:12px; font-weight:600; cursor:pointer; display:inline-flex;align-items:center;gap:4px; transition:opacity .15s; }
.btn-retorno:disabled { opacity:.5; cursor:not-allowed; }
.btn-stock    { background:#d1e7dd; color:#0a3622; }
.btn-descarte { background:#f8d7da; color:#58151c; }
.btn-proveedor{ background:#cfe2ff; color:#084298; }
.btn-stock:hover    { background:#badbcc; }
.btn-descarte:hover { background:#f1aeb5; }
.btn-proveedor:hover{ background:#9ec5fe; }

.btn-retorno-confirm { border:none; border-radius:6px; padding:8px 20px; font-size:14px; font-weight:600; cursor:pointer; }

/* Dark mode retornos */
.app-wrapper.dark .tab-count-pending { background:#ffc107; color:#000; }
.app-wrapper.dark .badge-retorno-pendiente { background:#3a2f00; color:#ffc107; }
.app-wrapper.dark .badge-retorno-stock     { background:#0a3622; color:#75b798; }
.app-wrapper.dark .badge-retorno-descarte  { background:#58151c; color:#f1aeb5; }
.app-wrapper.dark .badge-retorno-proveedor { background:#084298; color:#9ec5fe; }
.app-wrapper.dark .btn-stock    { background:#0a3622; color:#75b798; }
.app-wrapper.dark .btn-descarte { background:#58151c; color:#f1aeb5; }
.app-wrapper.dark .btn-proveedor{ background:#084298; color:#9ec5fe; }
.app-wrapper.dark .row-pendiente { background: rgba(255,193,7,.04); }

/* Historial NC */
.motivo-td { max-width: 180px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

/* Tab actions bar */
.tab-actions-bar {
  display: flex;
  justify-content: flex-end;
  padding: 0 0 10px;
}
.btn-outline-sm {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  font-size: 13px;
  font-weight: 600;
  border: 1.5px solid #c09d62;
  color: #c09d62;
  background: transparent;
  border-radius: 8px;
  cursor: pointer;
  transition: background .15s, color .15s;
}
.btn-outline-sm:hover:not(:disabled) { background: #c09d62; color: #fff; }
.btn-outline-sm:disabled { opacity: .5; cursor: not-allowed; }
.app-wrapper.dark .btn-outline-sm { border-color: #c09d62; color: #c09d62; }
.app-wrapper.dark .btn-outline-sm:hover:not(:disabled) { background: #c09d62; color: #1e1e2e; }

/* ── Paginación ───────────────────────────────────────────────── */
.pagination-wrapper { display: flex; justify-content: space-between; align-items: center; padding: 1rem; border-top: 1px solid #dee2e6; }
.pagination-info { color: #6c757d; font-size: 0.875rem; }
.pagination-container { display: flex; gap: 5px; }
.pagination-button { padding: 6px 14px; border: 1px solid #dee2e6; background: white; cursor: pointer; border-radius: 6px; font-size: 0.875rem; font-family: "Montserrat", sans-serif; transition: all 0.3s ease; color: #212529; }
.pagination-button:hover:not(:disabled) { background: #c09d62; color: white; border-color: #c09d62; }
.pagination-button:disabled { opacity: 0.5; cursor: not-allowed; }
.app-wrapper.dark .pagination-wrapper { border-color: #404040; }
.app-wrapper.dark .pagination-info { color: #9e9e9e; }
.app-wrapper.dark .pagination-button { background: #2d2d2d; color: #e5e5e5; border-color: #404040; }
.app-wrapper.dark .pagination-button:hover:not(:disabled) { background: #c09d62; color: white; border-color: #c09d62; }
.app-wrapper.dark .accion-btn { opacity: .9; }
.app-wrapper.dark .accion-btn:hover:not(:disabled) { opacity: 1; }
</style>
